// Composable con helpers para Supabase adaptados a Nuxt
import type { RestauranteDashboard, AnalyticsRapidos } from '~/types/database'

// Mensajes de error en español, escritos para un hostelero (no para un dev).
// Microcopy audit 2026-07-15: nada de "base de datos", "usuario" ni "por favor".
// Estructura Nielsen H9: qué pasó + cómo salir de ahí.
const errorMessages: Record<string, string> = {
  'Invalid login credentials': 'Email o contraseña incorrectos. Revísalos y prueba otra vez.',
  'Email not confirmed': 'Confirma tu email antes de entrar. Te mandamos un correo con el enlace.',
  'User not found': 'No encontramos esa cuenta. Revisa el correo.',
  'Invalid email': 'Ese email no es válido. Revísalo.',
  'Password should be at least 6 characters': 'La contraseña necesita al menos 6 caracteres.',
  'User already registered': 'Este email ya está registrado. Entra con él o recupera la contraseña.',
  'Database connection error': 'No hay conexión con el servidor. Revisa tu internet y reintenta.',
  'Row Level Security policy violation': 'No tienes permisos para hacer esto. Pídeselo al dueño del local.',
  'JWT expired': 'Tu sesión ha caducado. Vuelve a entrar.',
  'Permission denied': 'No tienes permisos para hacer esto. Pídeselo al dueño del local.'
}

/** Lo que lee el usuario cuando no sabemos qué pasó. Nunca "Error desconocido". */
const ERROR_GENERICO = 'Algo salió mal. Reintenta en unos segundos.'

export const useSupabaseHelpers = () => {
  const client = useSupabaseClient()

  const handleError = (error: any) => {
    // El texto crudo de Postgres (inglés, jerga) va SOLO al canal técnico.
    reportarError(error, { scope: 'supabase.handleError', code: error?.code })
    const message = errorMessages[error?.message] || ERROR_GENERICO
    return {
      message,
      code: error.code,
      details: error.details,
      isAuthError: error.message?.includes('JWT') || error.message?.includes('credentials'),
      isPermissionError: error.message?.includes('Permission') || error.message?.includes('policy')
    }
  }

  // Queries del dashboard
  const getDashboardData = async (restauranteId: string) => {
    const { data, error } = await client.rpc('get_restaurant_dashboard', {
      restaurante_uuid: restauranteId
    })
    if (error) throw error
    return data as RestauranteDashboard
  }

  const getAnalyticsRapidos = async (restauranteId: string) => {
    const { data, error } = await client.rpc('get_analytics_rapidos', {
      restaurante_uuid: restauranteId
    })
    if (error) throw error
    return data as AnalyticsRapidos
  }

  const getPedidosActivos = async (restauranteId: string) => {
    const { data, error } = await client.rpc('get_pedidos_activos', {
      restaurante_uuid: restauranteId
    })
    if (error) throw error
    return data
  }

  const getResumenFinanciero = async (restauranteId: string, fechaInicio?: string, fechaFin?: string) => {
    const { data, error } = await client.rpc('resumen_financiero_restaurante', {
      restaurante_uuid: restauranteId,
      p_fecha_inicio: fechaInicio,
      p_fecha_fin: fechaFin
    })
    if (error) throw error
    return data
  }

  const getStatsRealTime = async (restauranteId: string) => {
    const { data, error } = await client.rpc('get_stats_tiempo_real', {
      restaurante_uuid: restauranteId
    })
    if (error) throw error
    return data
  }

  const getItemsEstacion = async (restauranteId: string, estacion?: string) => {
    const { data, error } = await client.rpc('get_items_estacion', {
      restaurante_uuid: restauranteId,
      estacion_param: estacion || null
    })
    if (error) throw error
    return data
  }

  // Suscripciones realtime
  const setupRealtimeSubscriptions = (restauranteId: string, callbacks: {
    onPedidoChange?: (payload: any) => void
    onItemChange?: (payload: any) => void
    onPagoChange?: (payload: any) => void
    onMesaChange?: (payload: any) => void
    onSesionChange?: (payload: any) => void
    onLlamadaChange?: (payload: any) => void
    onConnectionChange?: (connected: boolean) => void
  }) => {
    const channels: any[] = []

    if (callbacks.onPedidoChange) {
      const ch = client
        .channel(`pedidos-${restauranteId}`)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'pedidos',
          filter: `restaurante_id=eq.${restauranteId}`
        }, callbacks.onPedidoChange)
        .subscribe((status: string) => {
          if (callbacks.onConnectionChange) {
            callbacks.onConnectionChange(status === 'SUBSCRIBED')
          }
        })
      channels.push(ch)
    }

    if (callbacks.onItemChange) {
      const ch = client
        .channel(`items-${restauranteId}`)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'items_pedido'
        }, callbacks.onItemChange)
        .subscribe()
      channels.push(ch)
    }

    if (callbacks.onPagoChange) {
      const ch = client
        .channel(`pagos-${restauranteId}`)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'pagos'
        }, callbacks.onPagoChange)
        .subscribe()
      channels.push(ch)
    }

    if (callbacks.onMesaChange) {
      const ch = client
        .channel(`mesas-${restauranteId}`)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'mesas',
          filter: `restaurante_id=eq.${restauranteId}`
        }, callbacks.onMesaChange)
        .subscribe()
      channels.push(ch)
    }

    if (callbacks.onSesionChange) {
      const ch = client
        .channel(`sesiones-${restauranteId}`)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'sesiones_mesa',
          filter: `restaurante_id=eq.${restauranteId}`
        }, callbacks.onSesionChange)
        .subscribe()
      channels.push(ch)
    }

    if (callbacks.onLlamadaChange) {
      const ch = client
        .channel(`llamadas-${restauranteId}`)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'llamadas_camarero',
          filter: `restaurante_id=eq.${restauranteId}`
        }, callbacks.onLlamadaChange)
        .subscribe()
      channels.push(ch)
    }

    return () => {
      channels.forEach(channel => client.removeChannel(channel))
    }
  }

  return {
    handleError,
    getDashboardData,
    getAnalyticsRapidos,
    getPedidosActivos,
    getResumenFinanciero,
    getStatsRealTime,
    getItemsEstacion,
    setupRealtimeSubscriptions
  }
}
